import { useEffect, useState } from 'react';

/**
 * TimeInput permite introducir una hora absoluta en formato HH:mm:ss.
 * - Valida formato y límite inferior / superior.
 */
export type TimeInputProps = {
  initialValue: string; // HH:mm:ss
  minValue: string; // HH:mm:ss
  maxValue: string; // HH:mm:ss
  onConfirm: (formattedTime: string) => void;
};

function timeToSeconds(time: string): number {
  const [h, m, s] = time.split(':').map(Number);
  return h * 3600 + m * 60 + s;
}

export function TimeInput({ initialValue, minValue, maxValue, onConfirm }: TimeInputProps) {
  const [inputValue, setInputValue] = useState(initialValue);

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const validateAndFormat = (value: string): string | null => {
    const regex = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/; // HH:mm:ss con o sin ceros a la izquierda
    const match = value.match(regex);
    if (!match) return null;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);

    if (hours > 23 || minutes > 59 || seconds > 59) return null;

    const candidateSeconds = timeToSeconds(
      `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
    );
    if (candidateSeconds < timeToSeconds(minValue) || candidateSeconds > timeToSeconds(maxValue)) {
      return null;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const confirmInput = () => {
    const formatted = validateAndFormat(inputValue);
    if (formatted) {
      setInputValue(formatted);
      onConfirm(formatted);
    } else {
      setInputValue(initialValue);
    }
  };

  return (
    <input
      type="text"
      value={inputValue}
      onChange={e => setInputValue(e.target.value)}
      onBlur={confirmInput}
      onKeyDown={e => {
        if (e.key === 'Enter') confirmInput();
      }}
      placeholder="HH:mm:ss"
      inputMode="numeric"
      className="h-[25px] w-[80px] text-basic"
    />
  );
}

export default TimeInput;
